import { Box, IconButton, styled, Typography, typographyClasses } from '@mui/material';
import React from 'react';
import type { Column } from 'react-table';
import { IconAccessTime, IconWarning } from 'assets/img';
import { ContextMenuButton, LwPopover } from 'redesign';
import { TFunctionNonStrict } from 'i18next';
import { DateTime } from 'luxon';
import { PaymentsCellProp, PaymentsColumnDefinition } from './payments.types';

export const getPaymentColumnDefinition: (
  t: TFunctionNonStrict<'hq-screen-specific', undefined>
) => Column<PaymentsColumnDefinition>[] = (t) => {
  const workerName: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.flexworker_name', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'workerName',
    Cell: WorkerCell,
  };

  const paymentId: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.payment_id', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'paymentId',
    Cell: PaymentIdCell,
  };

  const creationDate: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.creation_date', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'creationDate',
    width: '150px',
    Cell: DateCell,
  };

  const invoice: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.invoice', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'invoice',
    width: '150px',
    Cell: InvoiceCell,
  };

  const failureReason: Column<PaymentsColumnDefinition> = {
    accessor: 'failureReason',
    width: '50px',
    Cell: FailReasonCell(t),
  };

  const status: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.status', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'status',
    width: '150px',
    Cell: PaymentStatusCell,
  };

  const dueDate: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.due_date', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'dueDate',
    width: '150px',
    Cell: DateCell,
  };

  const actions: Column<PaymentsColumnDefinition> = {
    Header: () => (
      <Header>{t('payment.table_headers.actions', { ns: 'hq-screen-specific' })}</Header>
    ),
    accessor: 'actions',
    width: '150px',
    Cell: ActionCell,
  };

  return [workerName, paymentId, creationDate, invoice, failureReason, status, dueDate, actions];
};

const Header = ({ children }: React.PropsWithChildren<unknown>) => (
  <Typography variant="body2" style={{ fontWeight: 'bold' }}>
    {children}
  </Typography>
);

const WorkerCell = (cell: PaymentsCellProp<'workerName'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const PaymentIdCell = (cell: PaymentsCellProp<'paymentId'>) => {
  return (
    <Typography variant="body1" noWrap>
      {cell.value}
    </Typography>
  );
};

const DateCell = (cell: PaymentsCellProp<'creationDate' | 'dueDate'>) => {
  {
    return (
      <BoxInfo>
        <Typography variant="body1" color="secondary">
          {cell.value ? cell.value.toFormat('dd MMM yyyy') : null}
        </Typography>
        <BoxTime>
          <IconAccessTime />
          <Typography variant="body1">
            {cell.value ? cell.value.toFormat('HH:mm') : null}
          </Typography>
        </BoxTime>
      </BoxInfo>
    );
  }
};
const InvoiceCell = (cell: PaymentsCellProp<'invoice'>) => {
  return (
    <Box>
      {cell.value ? (
        <a href={cell.value.invoiceUrl} download>
          <Typography>{cell.value.invoiceReference}</Typography>
        </a>
      ) : null}
    </Box>
  );
};

const FailReasonCell =
  (t: TFunctionNonStrict<'hq-screen-specific', undefined>) =>
  (cell: PaymentsCellProp<'failureReason'>) => {
    const { reason, date } = cell.value || {};

    if (!reason || !date) {
      return null;
    }

    const formattedDate = DateTime.fromISO(date).toFormat('dd MMMM yyyy, HH:mm');

    return (
      <LwPopover
        triggerButton={({ onClick }) => (
          <IconButton aria-label="failure-reason" data-testid="failure-icon" onClick={onClick}>
            <StyledIcon as={IconWarning} />
          </IconButton>
        )}
        id="failure-reason-popover"
      >
        <StyledPopoverContainer>
          <StyledFailedReasonTitle>
            {t('payment.failureReason.title', { ns: 'hq-screen-specific' })}
          </StyledFailedReasonTitle>
          <StyledReason data-testid="failure-reason">{reason}</StyledReason>
          <StyledDate data-testid="failure-date">{formattedDate}</StyledDate>
        </StyledPopoverContainer>
      </LwPopover>
    );
  };

const PaymentStatusCell = (cell: PaymentsCellProp<'status'>) => {
  return <Typography variant="body1">{cell.value}</Typography>;
};

const ActionCell = (cell: PaymentsCellProp<'actions'>) => {
  const actions = cell.getRowActions(cell.value.paymentId);
  return <ContextMenuButton>{actions}</ContextMenuButton>;
};

const BoxInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  alignItems: 'flexStart',
}));

const BoxTime = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'end',
  gap: theme.spacing(0.5),
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwDanger[100],
    strokeWidth: theme.spacing(0.5),
  },
}));

const StyledFailedReasonTitle = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontWeight: 'bold',
    fontSize: theme.typography.pxToRem(18),
    marginBottom: theme.spacing(5),
  },
}));

const StyledReason = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(15),
    marginBottom: theme.spacing(1),
  },
}));

const StyledDate = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.lwBlack[40],
    fontWeight: 500,
  },
}));

const StyledPopoverContainer = styled(Box)(() => ({
  maxWidth: '320px',
}));
